import { CrmAudienceDefinition } from './CrmAudienceDefinition';

/** How a list got its members (PAUL-3691). */
export enum CrmAudienceListKind {
  /** Members follow the saved definition (today's lists). */
  Filter = 0,
  /** Members are fixed at upload: the addresses of a file or a pasted list. */
  Upload = 1,
}

/**
 * Audience list (handover §8) — a saved definition + a membership snapshot. Since PAUL-2997
 * this is the ONE saved audience object: it both narrows a campaign's recipients and targets
 * individual newsletter sections.
 */
export interface CrmAudienceList {
  id: string;
  name: string;
  snapshot_count: number;
  refreshed_at: string;
  /**
   * false = a filterless "every subscriber" list: valid as a campaign audience, rejected as a
   * section target. Follows the MATCHER, not `definition.filters.length` — a filter the matcher
   * can't use (an empty `values` array, an operator the dimension doesn't support) constrains
   * nobody and reports false. Never recompute this on the client; the two disagree. An upload
   * list reports false too.
   */
  has_filters: boolean;
  /** Absent on a backend that predates PAUL-3691, which reads as a filter list. */
  kind?: CrmAudienceListKind;
  /** The uploaded file's name; null for pasted addresses and for a filter list. */
  upload_file_name?: string | null;
}

export interface CrmAudienceListDetail extends CrmAudienceList {
  /** null/empty = "everyone (subscribed, non-suppressed)". */
  definition: CrmAudienceDefinition | null;
  created_at: string;
}

/** Create / update body. `definition: null` on PUT = rename-only. */
export interface CrmAudienceListUpsert {
  name: string;
  definition?: CrmAudienceDefinition | null;
}

/**
 * The answer to an upload (PAUL-3691): the new list and what happened to the rows, with
 * `rows = matched + created + skipped` (skipped = no usable address, or a duplicate within the upload).
 */
export interface CrmAudienceListUploadResult {
  list: CrmAudienceListDetail;
  rows: number;
  matched: number;
  created: number;
  skipped: number;
  subscriptions_created: number;
  members: number;
}
